import {
	MULTIPART_PART_SIZE_BYTES,
	MultipartAbortReason,
	MultipartFailureKind,
	MultipartFallbackReason,
	MultipartLocalSizeMismatchDirection,
	MultipartReconciliationVerdict,
	MultipartSessionStatus,
} from '@atlas/types'
import { logger } from '@/lib/logger'
import type { ClassifyTuple } from '@/services/classification.service'
import type { FileDTO } from '@/services/file.service'
import type { MultipartPartsResponse, UploadPartPresign } from '@/services/file-multipart.service'
import { fileMultipartService } from '@/services/file-multipart.service'
import { UploadRecoveryAction } from '../../upload-recovery'
import { MultipartUploadError, multipartErrorCode, multipartErrorDetails } from './multipart-errors'
import { cancelledAttemptError, failureMessage, observeCancellation, observeSession } from './multipart-finishing'
import type { StoredMultipartSession } from './multipart-session-storage'
import { findSession, forgetSession, rememberSession } from './multipart-session-storage'

const log = logger.child('multipart-session')

/** At most this many `DELETE` attempts in one interaction when the local file disagrees with the session's size. */
export const MULTIPART_LOCAL_MISMATCH_ABORT_RETRY_LIMIT = 3

/**
 * A `Discard`/`Cancel` thunk for a session the user has chosen to give up on.
 *
 * `DELETE` is retried until the session actually reads `ABORTED`, because that
 * is the state that releases the File's in-flight invariant — a session left
 * `ABORTING` still blocks a fresh attempt. A vanished S3 multipart is
 * success-equivalent and the backend already reports it as `ABORTED`.
 */
export function releaseSessionFn(fileId: string, sessionId: string, file: File): () => Promise<void> {
	return async () => {
		for (let attempt = 1; attempt <= MULTIPART_LOCAL_MISMATCH_ABORT_RETRY_LIMIT; attempt++) {
			// No diagnostic reason: `reason`/`direction` are the local-size-mismatch
			// counter's inputs, and a user-initiated discard is not that outcome.
			const result = await fileMultipartService.abort(fileId, sessionId)
			if (result.status === MultipartSessionStatus.ABORTED) {
				forgetSession(file)
				return
			}
		}
		throw new MultipartUploadError(
			"Couldn't discard the previous upload. Please try again.",
			MultipartFailureKind.RETRYABLE,
			{
				multipartSessionId: sessionId,
				actions: [UploadRecoveryAction.DISCARD, UploadRecoveryAction.CLOSE],
			},
		)
	}
}

/**
 * A multipart attempt that must continue as a single PUT. Thrown internally and
 * converted by `MultipartUploader` into the strategy's fallback verdict — a
 * strategy never invokes another strategy.
 */
export class MultipartFallbackSignal extends Error {
	constructor(public readonly reason: MultipartFallbackReason) {
		super(`Multipart cannot continue: ${reason}`)
		this.name = 'MultipartFallbackSignal'
	}
}

/** The session this upload will transfer against, plus what S3 already holds for it. */
export interface ResolvedMultipartSession {
	multipartSessionId: string
	declaredFileSize: number
	partSize: number
	partCount: number
	/** URLs the control plane already returned, so the first part needs no extra round trip. */
	seeded: UploadPartPresign[]
	/** Part numbers still to upload — missing plus wrong-sized, and every part for a new session. */
	remaining: number[]
	/** Parts S3 already holds at their expected size; the resume progress baseline. */
	alreadyUploaded: { partNumber: number; size: number }[]
}

export type SessionResolution =
	| { kind: 'session'; session: ResolvedMultipartSession }
	/** A prior attempt already finished; the upload resolves without transferring anything. */
	| { kind: 'confirmed'; file: FileDTO }

export interface SessionInput {
	fileId: string
	file: File
	classify?: ClassifyTuple[]
}

/**
 * Resolves the session this upload continues or starts.
 *
 * Local metadata is only ever an identity hint: what is resumable, and which
 * parts are outstanding, comes from the backend's read of S3. Local state that
 * the server contradicts is discarded rather than trusted.
 */
export async function resolveSession(input: SessionInput): Promise<SessionResolution> {
	const stored = findSession(input.file)
	if (stored && stored.fileId === input.fileId) return resumeStoredSession(input, stored)
	return createSession(input)
}

/** Creates a fresh attempt. One part is presigned with it — the rest are requested on demand. */
async function createSession(input: SessionInput): Promise<SessionResolution> {
	const uploadAttemptId = crypto.randomUUID()
	try {
		const response = await fileMultipartService.createOrPresign(input.fileId, {
			uploadAttemptId,
			partNumbers: [1],
			...(input.classify && input.classify.length > 0 ? { classify: input.classify } : {}),
		})
		if (input.file.size !== response.declaredFileSize) {
			await abortSizeMismatch(input, response.multipartSessionId, response.declaredFileSize)
		}
		rememberSession(input.file, {
			fileId: input.fileId,
			multipartSessionId: response.multipartSessionId,
			uploadAttemptId,
			originalName: input.file.name,
			declaredFileSize: response.declaredFileSize,
			lastModified: input.file.lastModified,
			partSize: response.partSize,
			partCount: response.partCount,
		})
		return {
			kind: 'session',
			session: {
				multipartSessionId: response.multipartSessionId,
				declaredFileSize: response.declaredFileSize,
				partSize: response.partSize,
				partCount: response.partCount,
				seeded: response.uploadParts,
				remaining: allPartNumbers(response.partCount),
				alreadyUploaded: [],
			},
		}
	} catch (error) {
		return handleCreateFailure(input, error)
	}
}

/** The eligibility answers that are safe to continue as a single PUT, and the ones that are not. */
async function handleCreateFailure(input: SessionInput, error: unknown): Promise<SessionResolution> {
	if (error instanceof MultipartFallbackSignal) throw error
	const code = multipartErrorCode(error)
	// Both mean the backend created no session and no S3 multipart, so no competing
	// PUT can race a multipart that might still complete.
	if (code === 'MULTIPART_PUBLIC_FILE_NOT_SUPPORTED') {
		throw new MultipartFallbackSignal(MultipartFallbackReason.PUBLIC_FILE_NOT_SUPPORTED)
	}
	if (code === 'FILE_DECLARED_SIZE_REQUIRED')
		throw new MultipartFallbackSignal(MultipartFallbackReason.DECLARED_SIZE_REQUIRED)
	// Single PUT would fail the same validation at confirmation, so falling back
	// would only move the same rejection later.
	if (code === 'CLASSIFICATION_INVALID') throw error
	if (code === 'MULTIPART_SESSION_CONFLICT') return resolveConflict(input, error)
	throw error
}

/**
 * `409 MULTIPART_SESSION_CONFLICT` — a live session already owns this File.
 * Atlas never silently aborts one, so each state has its own recovery, and
 * resume is offered only when this tab holds that exact session's identity.
 */
async function resolveConflict(input: SessionInput, error: unknown): Promise<SessionResolution> {
	const details = multipartErrorDetails(error)
	const status = details.status as MultipartSessionStatus | undefined
	const multipartSessionId = typeof details.multipartSessionId === 'string' ? details.multipartSessionId : undefined
	log.warn('Multipart session conflict', { status, multipartSessionId })

	if (status === MultipartSessionStatus.COMPLETING || status === MultipartSessionStatus.COMPLETED) {
		if (!multipartSessionId) throw error
		// The newly selected file is not uploaded: the prior session is recovered
		// first, and recovery starts with a READ — a finalizer holding a fresh
		// lease is waited out, never raced with another completion call.
		return { kind: 'confirmed', file: await observeSession({ fileId: input.fileId, sessionId: multipartSessionId }) }
	}

	const stored = findSession(input.file)
	if (status === MultipartSessionStatus.ACTIVE && stored && stored.multipartSessionId === multipartSessionId) {
		return resumeStoredSession(input, stored)
	}
	// No local identity for that session: resume is not offered, and a
	// same-size file is never accepted as the original. Discard releases
	// the blocking session first; Start over abandons this file's attempt.
	throw new MultipartUploadError(
		'Another upload for this file is still in progress in a different tab or session. Discard it or start over.',
		MultipartFailureKind.RESTART_REQUIRED,
		{
			code: 'MULTIPART_SESSION_CONFLICT',
			status,
			multipartSessionId,
			actions: [UploadRecoveryAction.DISCARD, UploadRecoveryAction.START_OVER],
			...(multipartSessionId ? { releaseSession: releaseSessionFn(input.fileId, multipartSessionId, input.file) } : {}),
		},
	)
}

/**
 * The session is owned by something other than this transfer — finalization, or
 * a cancellation — so it is watched rather than uploaded into. `null` means the
 * session is this upload's to continue.
 *
 * Both windows are read-first and hand on the read that established the state,
 * so entering one costs no second request. Neither issues a mutation: a
 * finalizer holding a fresh lease is waited out rather than raced,
 * and a cancellation may belong to another tab or flow, so this client observes
 * it and never takes over its `DELETE`.
 */
async function observeIfOwned(
	fileId: string,
	sessionId: string,
	state: MultipartPartsResponse,
): Promise<SessionResolution | null> {
	if (state.status === MultipartSessionStatus.ABORTING) {
		return {
			kind: 'confirmed',
			file: await observeCancellation({ fileId, sessionId, status: state.status, observed: state }),
		}
	}
	if (FINALIZING_STATUSES.has(state.status)) {
		return { kind: 'confirmed', file: await observeSession({ fileId, sessionId, observed: state }) }
	}
	return null
}

/** Reconciles a stored session against the server's read of S3 and returns the outstanding work. */
async function resumeStoredSession(input: SessionInput, stored: StoredMultipartSession): Promise<SessionResolution> {
	const state = await fileMultipartService.getParts(input.fileId, stored.multipartSessionId)
	if (state.status === MultipartSessionStatus.CONFIRMED && state.file) {
		forgetSession(input.file)
		return { kind: 'confirmed', file: state.file }
	}
	assertResumable(input, state)

	const watched = await observeIfOwned(input.fileId, stored.multipartSessionId, state)
	if (watched) return watched

	if (input.file.size !== state.declaredFileSize) {
		// The existing session is preserved untouched — it is the selected file that
		// is wrong, not the upload (the resume-mismatch rule).
		// No recovery action: the upload being resumed is untouched and still
		// correct, so the only thing to fix is which file was selected. The tile's
		// existing Remove affordance dismisses the rejected selection.
		throw new MultipartUploadError(
			'This file does not match the upload being resumed. Select the original file.',
			MultipartFailureKind.FILE_MISMATCH,
			{ multipartSessionId: stored.multipartSessionId, actions: [] },
		)
	}

	const outstanding = [
		...(state.missingPartNumbers ?? []),
		...(state.invalidParts ?? []).map((part) => part.partNumber),
	]
	return {
		kind: 'session',
		session: {
			multipartSessionId: stored.multipartSessionId,
			declaredFileSize: state.declaredFileSize,
			partSize: state.partSize,
			partCount: state.partCount,
			seeded: [],
			remaining: outstanding.sort((a, b) => a - b),
			alreadyUploaded: state.completedParts ?? [],
		},
	}
}

/** A session that cannot be resumed clears its local metadata here, so the surface can only offer Start over. */
function assertResumable(input: SessionInput, state: MultipartPartsResponse): void {
	const gone =
		state.verdict === MultipartReconciliationVerdict.EXPIRE || state.status === MultipartSessionStatus.EXPIRED
	if (gone) {
		forgetSession(input.file)
		throw new MultipartUploadError(
			'This upload can no longer be resumed. Please start over.',
			MultipartFailureKind.RESTART_REQUIRED,
			{
				status: state.status,
				verdict: state.verdict,
				resumeCleared: true,
				actions: [UploadRecoveryAction.START_OVER],
			},
		)
	}
	if (state.status === MultipartSessionStatus.FAILED) {
		forgetSession(input.file)
		throw new MultipartUploadError(failureMessage(state.failureCode), MultipartFailureKind.RESTART_REQUIRED, {
			code: state.failureCode,
			status: state.status,
			resumeCleared: true,
			actions: [UploadRecoveryAction.START_OVER],
		})
	}
	if (state.status === MultipartSessionStatus.ABORTED) {
		// A cancelled attempt is terminal and holds no parts worth keeping a
		// pointer to, so the local metadata goes with it and only Start over
		// remains.
		forgetSession(input.file)
		throw cancelledAttemptError(state.multipartSessionId, true)
	}
}

/**
 * A brand-new session whose trusted size disagrees with the selected file:
 * upload no bytes, cancel the session, and continue as a single PUT only once
 * the abort has actually converged to `ABORTED`. While it has not, no competing
 * PUT may start.
 */
async function abortSizeMismatch(input: SessionInput, sessionId: string, declaredFileSize: number): Promise<never> {
	const direction =
		input.file.size > declaredFileSize
			? MultipartLocalSizeMismatchDirection.LOCAL_GT_DECLARED
			: MultipartLocalSizeMismatchDirection.LOCAL_LT_DECLARED
	for (let attempt = 1; attempt <= MULTIPART_LOCAL_MISMATCH_ABORT_RETRY_LIMIT; attempt++) {
		try {
			const result = await fileMultipartService.abort(input.fileId, sessionId, {
				reason: MultipartAbortReason.LOCAL_SIZE_MISMATCH,
				direction,
			})
			if (result.status === MultipartSessionStatus.ABORTED) {
				forgetSession(input.file)
				throw new MultipartFallbackSignal(MultipartFallbackReason.LOCAL_SIZE_MISMATCH)
			}
		} catch (error) {
			if (error instanceof MultipartFallbackSignal) throw error
			log.warn('Multipart abort after a size mismatch did not converge', { attempt })
		}
	}
	// The session is preserved and no competing single PUT may start until the
	// abort converges, so the only safe offers are another attempt or giving up.
	throw new MultipartUploadError("Couldn't switch upload method yet.", MultipartFailureKind.RETRYABLE, {
		multipartSessionId: sessionId,
		actions: [UploadRecoveryAction.RETRY, UploadRecoveryAction.CLOSE],
	})
}

export function allPartNumbers(partCount: number): number[] {
	return Array.from({ length: partCount }, (_unused, index) => index + 1)
}

/** Computes the part count the way the backend does, for a client that has not yet created a session. */
export function expectedPartCount(fileSize: number): number {
	return Math.ceil(fileSize / MULTIPART_PART_SIZE_BYTES)
}

/**
 * The File a resumable session already belongs to, or `null` when this upload
 * has to start a new one.
 *
 * Resume reuses the File the interrupted attempt created — a new File would
 * orphan the S3 multipart that still holds the transferred parts. Local
 * metadata alone never establishes that: the session is read back first, and
 * anything the server cannot confirm as still live is discarded here rather
 * than carried into the upload.
 */
export async function findResumableFileId(file: File): Promise<string | null> {
	const stored = findSession(file)
	if (!stored) return null
	try {
		const state = await fileMultipartService.getParts(stored.fileId, stored.multipartSessionId)
		if (RESUMABLE_STATUSES.has(state.status) && state.verdict !== MultipartReconciliationVerdict.EXPIRE) {
			return stored.fileId
		}
	} catch (error) {
		log.info('Stored multipart session could not be read back; discarding it', {
			multipartSessionId: stored.multipartSessionId,
			code: multipartErrorCode(error),
		})
	}
	forgetSession(file)
	return null
}

/** A session finalization already owns — re-entering one observes it, never re-transfers or re-completes it. */
const FINALIZING_STATUSES: ReadonlySet<MultipartSessionStatus> = new Set([
	MultipartSessionStatus.COMPLETING,
	MultipartSessionStatus.COMPLETED,
])

/**
 * Statuses a stored session may still be continued from. `CONFIRMED` is
 * included because the upload it belongs to finished: continuing it resolves
 * the same File rather than uploading the bytes a second time.
 */
const RESUMABLE_STATUSES: ReadonlySet<MultipartSessionStatus> = new Set([
	MultipartSessionStatus.ACTIVE,
	MultipartSessionStatus.COMPLETING,
	MultipartSessionStatus.COMPLETED,
	MultipartSessionStatus.CONFIRMED,
	// Not resumable as a transfer — continued only as read-only observation of the
	// cancellation that owns it, which is what keeps a status re-check from
	// silently starting a second upload of the same file behind an in-flight abort.
	MultipartSessionStatus.ABORTING,
])
